import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, MapPin } from "lucide-react";
import CinematicHero from "../components/CinematicHero";
import TrustStrip from "../components/TrustStrip";
import WhyBespoke from "../components/WhyBespoke";
import ServiceCards from "../components/ServiceCards";
import Transformation from "../components/Transformation";
import ProcessTimeline from "../components/ProcessTimeline";
import AuditIntro from "../components/AuditIntro";
import SqueegeeReveal from "../components/SqueegeeReveal";
import PressureWashReveal from "../components/PressureWashReveal";
import WaterFedPoleDemo from "../components/WaterFedPoleDemo";
import FAQ from "../components/FAQ";
import AnimatedCounter from "../components/AnimatedCounter";
import Testimonials from "../components/Testimonials";
import TrustpilotSection from "../components/TrustpilotSection";
import DroneInspectionIntro from "../components/DroneInspectionIntro";
import WeatherIntel from "../components/WeatherIntel";
import NewsletterCapture from "../components/NewsletterCapture";
import SiteWalkModal from "../components/SiteWalkModal";
import { useState } from "react";

const TESTIMONIALS = [
  { id: 1, q: "They treat our portfolio like it's their own storefront. The photo debriefs alone save my ops manager three hours a week.", by: "Retail Portfolio", role: "Multi-Location · NYC", rating: 5 },
  { id: 2, q: "Called them for a graffiti emergency on a Sunday. Crew was there in ninety minutes with the right chemistry.", by: "Facilities Team", role: "Charlotte · NC", rating: 5 },
  { id: 3, q: "Military-grade precision — literally. Everything is briefed, executed, and documented. First vendor that just handles it.", by: "Operations Lead", role: "Multi-Unit Hospitality", rating: 5 },
];

const CLIENT_LOGOS = [
  "SoHo Retail", "Bay Ridge Group", "Asheville Hospitality", "Manhattan Portfolio", "Charlotte Medical", "Raleigh Commercial", "Nassau Retail Trust",
];

export default function Home() {
  const [walkOpen, setWalkOpen] = useState(false);
  return (
    <div data-testid="home-page" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <SiteWalkModal open={walkOpen} onClose={() => setWalkOpen(false)} />
      {/* 1 — CINEMATIC HERO */}
      <CinematicHero />

      {/* 2 — INSTANT TRUST STRIP */}
      <TrustStrip />

      {/* 2b — TRUSTPILOT VERIFIED REVIEWS */}
      <TrustpilotSection />

      {/* 3 — WHY BESPOKE */}
      <WhyBespoke />

      {/* 3b — DRONE INSPECTION INTRO (cinematic sequence into services) */}
      <DroneInspectionIntro />

      {/* 4 — INTERACTIVE SERVICE CARDS */}
      <ServiceCards />

      {/* Numbers counter strip (light) — subtle proof */}
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ background: "var(--bg-2)" }} data-testid="counters-section">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { to: 12,  suf: "+",  label: "Years operating" },
              { to: 800, suf: "+",  label: "Commercial sites" },
              { to: 24,  suf: "/7", label: "Storm response" },
              { to: 100, suf: "%",  label: "Photo reported" },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="p-8 md:p-10"
                style={{ borderLeft: i === 0 ? "none" : "1px solid var(--line)" }}
              >
                <div className="font-display text-6xl md:text-7xl" style={{ color: "var(--text)", letterSpacing: "-0.04em", lineHeight: 0.95 }}>
                  <AnimatedCounter to={s.to} suffix={s.suf} />
                </div>
                <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — TRANSFORMATION GALLERY (Before/After slider) */}
      <Transformation />

      {/* 5b — SIGNATURE SQUEEGEE REVEAL (scroll-driven) */}
      <SqueegeeReveal />

      {/* 5c — PRESSURE WASH REVEAL (scroll-driven) */}
      <PressureWashReveal />

      {/* 5d — WATER-FED POLE DEMO (looping SVG) */}
      <WaterFedPoleDemo />

      {/* 5e — WEATHER INTELLIGENCE (dual-market forecast + best-day picker) */}
      <WeatherIntel />

      {/* 5f — STORM ALERT NEWSLETTER (touchpoint 1) */}
      <NewsletterCapture variant="storm" source="home-storm" />

      {/* 6 — YOUR PROCESS (4 steps) */}
      <ProcessTimeline />

      {/* 7 — AUDIT LENS (existing signature) */}
      <AuditIntro />

      {/* 8 — SOCIAL PROOF (testimonial carousel) */}
      <Testimonials />

      {/* 8b — Client Ledger marquee */}
      <section className="py-16 relative" style={{ background: "var(--bg)" }} data-testid="social-proof">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="text-center mb-10 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
            Trusted across the portfolios of
          </div>
          <div className="overflow-hidden">
            <div className="ticker-track">
              {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((l, i) => (
                <span key={i} className="font-display text-2xl md:text-3xl italic" style={{ color: "var(--text-2)", opacity: 0.65, letterSpacing: "-0.015em" }}>{l}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 9 — FAQ */}
      <FAQ />

      {/* 9b — MONTHLY REPORT NEWSLETTER (touchpoint 2 — main capture) */}
      <NewsletterCapture variant="inline" source="home-inline" />

      {/* 10 — PREMIUM CLOSING CTA */}
      <section className="py-32 md:py-44 relative overflow-hidden" style={{ background: "linear-gradient(180deg, var(--bg) 0%, #020204 100%)" }} data-testid="cta-section">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(900px 500px at 50% 60%, rgba(198,168,124,0.14), transparent 60%)",
        }} />

        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="overline mb-8">Ready when you are</div>
            <h2 className="font-display text-5xl md:text-8xl mx-auto max-w-5xl" style={{ lineHeight: 0.95, letterSpacing: "-0.04em", color: "var(--text)" }}>
              Hand off the exterior.
              <br />
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Keep the momentum.</span>
            </h2>
            <p className="mt-10 font-body text-lg md:text-xl mx-auto max-w-2xl" style={{ color: "var(--text-2)" }}>
              Instant plans from $199/mo · Founding 100 rate locked for life · Cancel anytime.
            </p>
            <div className="mt-14 flex flex-wrap justify-center gap-4">
              <Link to="/founding-partner" data-testid="cta-founding-btn" className="btn-glow" data-cursor="hover">
                Founding 100 · Lock $297/mo <ArrowUpRight size={13} />
              </Link>
              <button onClick={() => setWalkOpen(true)} data-testid="cta-site-walk-btn" className="btn-ghost" data-cursor="hover">
                Book a Site Walk · 30 min
              </button>
              <Link to="/pricing" data-testid="cta-pricing-btn" className="btn-ghost">
                See Instant Plans
              </Link>
              <Link to="/audit" data-testid="cta-audit-btn" className="btn-ghost">
                AI Property Audit
              </Link>
            </div>
            <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
              <div className="flex items-center gap-2"><MapPin size={11} style={{ color: "var(--gold)" }} /> New York City</div>
              <div className="flex items-center gap-2"><MapPin size={11} style={{ color: "var(--gold)" }} /> North Carolina</div>
              <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ade80" }} /> Available 24/7</div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
